import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { PartnerAccess } from '../../../../partner/access/partner-access';
import { PartnerAccessAdminService } from '../../../../../core/services/partner-portal/partner-access-admin.service';

/** MSP and MMSP account type ids. */
const ELIGIBLE_ACCOUNT_TYPES = new Set([2, 3]);

const NONE = { portal: false, sales: false };

export interface PartnerAccessChange {
  portal: boolean;
  sales: boolean;
}

/**
 * Portal and Sales toggles for one user. They only hold the choice; the user editor saves
 * `pendingChange()` after it saves the user, so a new primary org applies first.
 *
 * Eligibility follows the org picked in the form (`accountType`), falling back to the saved
 * org's. A user holding a grant always sees the toggles, so it can be revoked even after their
 * org stops being an MSP or MMSP.
 */
@Component({
  selector: 'cyflare-partner-access',
  standalone: true,
  imports: [InputsModule, LabelModule],
  template: `
    @if (access(); as a) {
      @if (a.eligible || saved().portal) {
        <div class="k-form-field mt-3">
          <kendo-label
            labelCssClass="k-form-label"
            text="Partner Portal"
          ></kendo-label>
          <div class="d-flex align-items-center gap-2 mb-2">
            <kendo-switch
              [checked]="a.portal"
              [disabled]="!a.eligible && !a.portal"
              (valueChange)="setPortal($event)"
              aria-label="Portal"
            ></kendo-switch>
            <span>Portal: content, training and certifications</span>
          </div>
          <div class="d-flex align-items-center gap-2">
            <kendo-switch
              [checked]="a.sales"
              [disabled]="!a.eligible && !a.sales"
              (valueChange)="setSales($event)"
              aria-label="Sales"
            ></kendo-switch>
            <span>Sales: deal registration (turns on Portal)</span>
          </div>
          @if (!a.eligible) {
            <kendo-formhint>
              This user's organization isn't an MSP or MMSP, so access can only be removed.
            </kendo-formhint>
          }
        </div>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerAccessComponent {
  private readonly service = inject(PartnerAccessAdminService);
  private readonly destroyRef = inject(DestroyRef);

  readonly userId = input<number | null>();
  readonly accountType = input<number | null>(null);

  private readonly loaded = signal<PartnerAccess | null>(null);
  private readonly draft = signal<PartnerAccessChange | null>(null);

  /** The access as saved: the user's current grant, or none for a user not added yet. */
  readonly saved = computed<PartnerAccessChange>(() => {
    const loaded = this.loaded();
    return loaded ? { portal: loaded.portal, sales: loaded.sales } : NONE;
  });

  readonly access = computed<PartnerAccess | null>(() => {
    if (this.userId() && !this.loaded()) return null;
    const accountType = this.accountType();
    const eligible = accountType != null ? ELIGIBLE_ACCOUNT_TYPES.has(accountType) : !!this.loaded()?.eligible;
    return { eligible, ...(this.draft() ?? this.saved()) };
  });

  /** What to save with the user, or null when the toggles weren't changed. */
  readonly pendingChange = computed<PartnerAccessChange | null>(() => {
    const draft = this.draft();
    const saved = this.saved();
    return draft && (draft.portal !== saved.portal || draft.sales !== saved.sales) ? draft : null;
  });

  constructor() {
    effect(
      () => {
        const userId = this.userId();
        this.loaded.set(null);
        this.draft.set(null);
        if (!userId) return;
        this.service
          .get(userId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({ next: (access) => this.loaded.set(access), error: () => this.loaded.set(null) });
      },
      { allowSignalWrites: true }
    );
    // A different org starts the choice over, so a grant picked for one org isn't carried to the next.
    effect(
      () => {
        this.accountType();
        untracked(() => this.draft.set(null));
      },
      { allowSignalWrites: true }
    );
  }

  /** Turning Portal off also turns Sales off. */
  setPortal(on: boolean): void {
    this.draft.set({ portal: on, sales: on && !!this.access()?.sales });
  }

  setSales(on: boolean): void {
    this.draft.set({ portal: true, sales: on });
  }
}
